{
  "title": "Events platform overview",
  "description": "What the platform's event pages offer, who can use them, how they differ from competitions and how data is protected.",
  "headings": [
    "What you will learn",
    "Who this is for",
    "Before you start",
    "Services at a glance",
    "Key guarantees",
    "FAQ"
  ],
  "summary": "The events platform is a separate set of features, running alongside competitions, that provides on-site services for conferences and similar events, for example TAAI 2026. Registration, payment and banquet entitlement stay with the official registration system; this platform only adds supplementary services.",
  "roleNote": "Attendees, event staff and platform administrators",
  "prerequisites": [
    "Open event pages from Events in the main navigation, at /events and /events/:slug",
    "Events and competitions are independent in identity, data and authorization: you do not need to compete to use event services, and competition organizers or judges do not automatically get event access",
    "Most services need a signed-in account with a verified email; shuttle booking can alternatively be completed with just an email"
  ],
  "pathLabel": "Main navigation → Events → choose the event",
  "steps": [
    {
      "title": "1. Free shuttle reservation",
      "description": "Reserve seats by day and trip, with just an email or after signing in, up to 4 seats at once (you plus up to 3 companions), each with its own ride QR. Full trips have a waitlist with automatic promotion and notification. Booking and cancellation close 12 hours before departure. See \"How to reserve an event shuttle seat\"."
    },
    {
      "title": "2. On-site QR check-in and passenger list",
      "description": "Staff check riders in by camera or pasted credential, can queue scans on the device when the network is poor and sync afterward; the server prevails and one QR succeeds only once. The passenger list supports manual cross-checking and shows no mobile numbers. See \"How to check in riders and use the passenger list on event day\"."
    },
    {
      "title": "3. Meals and banquet reply",
      "description": "Reply about lunch attendance, dietary category and banquet attendance so the organizer can estimate numbers; a banquet reply is not entitlement and not proof of payment. See \"How to reply about meals and use the help desk\"."
    },
    {
      "title": "4. Notices and help desk",
      "description": "The organizer publishes bilingual notices (general, important, critical) and can email people who hold a shuttle booking; attendees can send cases to the help desk and follow their status."
    },
    {
      "title": "5. Volunteers, posters and speakers",
      "description": "The organizer assigns volunteer shifts by email; poster presenters can request equipment; speakers can upload PDF or PPTX slides, which are always quarantined and downloadable only after the organizer reviews them."
    },
    {
      "title": "6. Event members and the organizer console",
      "description": "The organizer grants least-privilege access with four roles (Owner, Manager, Scanner, Viewer) and sees live booking, waitlist and check-in totals in the console. See \"How to manage event members and the organizer console\"."
    },
    {
      "title": "7. What each event member can do",
      "description": "Owner: everything, including member management, operations tools and emergency contact phones. Manager: notices, email notifications, meal export and help desk cases. Scanner: shuttle check-in and the passenger list. Viewer: reports only. Members are added by verified email, can be added early and become active after they register and verify; up to 50 per event, and the last Owner cannot be removed or demoted. Competition organizers do not automatically become event members."
    },
    {
      "title": "8. The platform administrator's role",
      "description": "A platform super admin is treated as Owner of every published event and can open the console and operations tools. Member changes, notices, case status, meal exports, emergency phone views and speaker file reviews each leave an event audit record. Privileged accounts such as platform administrators cannot book through the email link and must use normal sign-in. Events are created by the platform, which also keeps the automatic purge and retention rules in force. See the Events chapter of the Platform Administration manual for details."
    }
  ],
  "afterward": [
    "Seats are never oversold; one active booking per person per day per direction",
    "Personal data is limited to riding checks and urgent contact: check-in staff see only name, organization and job title, the mobile number is viewable only by the event Owner in an emergency and logged, and dietary notes are stored encrypted",
    "Rider contact, meal and reminder data are deleted 30 days after the event",
    "Hiding something on an event page is not the security boundary; every permission is evaluated on the server"
  ],
  "nextSteps": "To book a shuttle see \"How to reserve an event shuttle seat\"; for data handling see the Privacy Notice and Data Policy; for full procedures see the Events chapter of each role's manual.",
  "faq": [
    {
      "q": "How do events differ from competitions?",
      "a": "Competitions handle registration and teams, submissions, judging and results; events handle on-site services such as shuttles, meals and the help desk. Their data and permissions are separate and they share no roles."
    },
    {
      "q": "Do I need an account?",
      "a": "Not to browse event information. Shuttle booking can be done with just an email, and the seat is held only once you open the confirmation link, which also signs you in; meal replies and the help desk need sign-in."
    },
    {
      "q": "Does the platform handle registration and payment?",
      "a": "No. Registration, payment and banquet entitlement follow the official registration system; the platform offers no ticketing, payment, refund or course features."
    },
    {
      "q": "Which events are available?",
      "a": "Currently TAAI 2026 (/events/taai-2026). Events are created by the platform operators; there is no self-service screen for creating one."
    }
  ]
}
